import React, { useCallback, useContext, useEffect } from "react";
import { useSelector, useStore } from "react-redux";
import { usePrevious } from "react-use";
import { Feature, Polygon } from "geojson";
import mapboxgl from "mapbox-gl";

import { SELECTION_DRAW_POLYGON } from "fond/draw/modes/base/selectionDrawPolygon";
import { MapContext } from "fond/map/MapProvider";
import SelectionPolygon from "fond/map/SelectionPolygon";
import { EditMode, setEditMode } from "fond/project/redux";
import { clearSelection, completeDrawing, selectSelectionState, startDrawing } from "fond/redux/selection";
import { Store } from "fond/types";
import { useAppDispatch } from "fond/utils/hooks";

/**
 * Manages the draw lifecycle for the legend's selection polygon & renders the
 * completed selection until it is cleared.
 */
const SelectionHandler: React.FC = () => {
  const dispatch = useAppDispatch();
  const store = useStore<Store>();
  const editMode = useSelector((state: Store) => state.project.editMode);
  const prevEditMode = usePrevious(editMode);
  const projectId = useSelector((state: Store) => state.project.projectId);
  const selection = useSelector(selectSelectionState);
  const { map, drawControl, setDrawMode } = useContext(MapContext);

  /**
   * Finishing the polygon via double-click fires a trailing dblclick that
   * would zoom the map; suppress the default zoom for just that event.
   */
  const suppressNextDoubleClickZoom = useCallback(() => {
    if (!map) return;
    const suppressZoom = (e: mapboxgl.MapMouseEvent) => e.preventDefault();
    map.once("dblclick", suppressZoom);
    setTimeout(() => map.off("dblclick", suppressZoom), 500);
  }, [map]);

  const handleDraw = (polygon: Feature<Polygon>) => {
    suppressNextDoubleClickZoom();
    dispatch(completeDrawing(polygon));
    dispatch(setEditMode(EditMode.none));
  };

  const handleCancel = () => {
    // ignore spurious onCancel events that occur while we're not drawing
    if (selectSelectionState(store.getState()).status !== "drawing") return;
    dispatch(clearSelection());
    // Tearing down because the user switched to another edit mode also cancels the
    // draw; only reset the edit mode when the user is still selecting (e.g. Escape).
    if (store.getState().project.editMode === "select") {
      dispatch(setEditMode(EditMode.none));
    }
  };

  const startSelecting = () => {
    dispatch(startDrawing(projectId));
    setDrawMode(SELECTION_DRAW_POLYGON, { onDraw: handleDraw, onCancel: handleCancel });
  };

  /**
   * Tear down the drawing session & any incomplete selection
   */
  const tearDown = () => {
    // Delete any in-progress feature before exiting the mode
    if (drawControl.current?.getMode() === SELECTION_DRAW_POLYGON) {
      drawControl.current.deleteAll();
    }
    setDrawMode("no_feature");
    if (selectSelectionState(store.getState()).status === "drawing") {
      dispatch(clearSelection());
    }
  };

  useEffect(
    () => {
      return () => {
        tearDown();
      };
    },
    // Tear down on unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Monitor changes in edit mode:
  // - Start selecting if we enter the "select" mode, and
  // - Tear down the selection if we leave the "select" mode.
  // - Clear the selection if we enter any other edit mode while a selection is active.
  useEffect(
    () => {
      if (prevEditMode !== "select" && editMode === "select") {
        startSelecting();
      } else if (prevEditMode === "select" && editMode !== "select") {
        tearDown();
      } else if (editMode !== "none" && selectSelectionState(store.getState()).status !== "none") {
        dispatch(clearSelection());
      }
    },
    // We only want to trigger this effect when the editMode changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editMode]
  );

  return selection.status === "selected" ? <SelectionPolygon polygon={selection.polygon} /> : null;
};

export default SelectionHandler;
